import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { activeConversation } from "../auth/activeConversation";
import { GeneratedFilePreview } from "../components/GeneratedFilePreview";
import { GeneratedVideoPreview } from "../components/GeneratedVideoPreview";
import { MarkdownMessage, type MarkdownFileLinkContext } from "../components/MarkdownMessage";
import { stripCitationMarkers } from "../lib/citationMarkers";
import { useDictation } from "../voice/dictation";
import { useI18n } from "../i18n";
import type { AgentFile, AgentInstance, SessionEvent, SessionOutput, SessionRecord, UploadedFile } from "../types/api";
import { failedSessionText, terminalFailureEvent } from "../lib/failure";
import { filePreviewKind, splitFilesByRole, stillPendingFormats, resolveLinkedAgentFile } from "../lib/filePreview";
import { scrollTranscriptToBottom } from "../lib/chatScroll";
import { recoveryEventAction, RESULT_RECOVERY_WATCH_MS } from "../lib/resultRecovery";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  attachments?: string[];
  files?: AgentFile[];
  /** The user asked for the source material: show the reference group expanded. */
  showReferences?: boolean;
  /** Requested formats not produced in the turn that asked for them (neutral note, not a failure). */
  pendingFormats?: string[];
  progress?: AgentProgress[];
  // The agent's multi-step plan: seeded from an `agent.plan` event (all steps
  // pending) and marked done as `agent.step` events land, so the user watches
  // progress arrive step-by-step (the Telegram-agent feel) instead of waiting
  // silently for the synthesized answer.
  steps?: AgentStep[];
  objective?: string;
  streaming?: boolean;
  // True for messages loaded from a stored conversation (vs. produced live this
  // session). History messages defer their generated-file previews behind a
  // "Show preview" button so opening/paging a thread doesn't fetch+render every
  // file at once; live turns preview eagerly.
  history?: boolean;
}

interface AgentProgress {
  id: number;
  icon: string;
  text: string;
  createdAt: string;
}

type AgentStepStatus = "pending" | "running" | "completed" | "failed" | "partial_timeout";

interface AgentStep {
  index: number;
  total: number;
  title: string;
  done: boolean;
  text: string;
  status: AgentStepStatus;
}

interface Props {
  // Conversation to open on mount (null = a fresh "new chat"). The parent
  // remounts this screen (via key) when the selection changes.
  initialConversationId?: string | null;
  // Called once when a brand-new conversation gets its id (first send).
  onConversationStarted?: (conversationId: string) => void;
  // Called when a turn is created/finishes so the history drawer can refresh.
  onSessionsChanged?: () => void;
  // Called when the resume window has elapsed while backgrounded — the parent
  // should drop to a fresh new chat (the old conversation stays in history).
  onResumeExpired?: () => void;
}

// Safety watchdog only. Cloud turns can run for minutes between assistant text
// chunks while still emitting progress/step events, so a turn is finished by
// session.completed/failed/cancelled (or the socket closing) — not by short idle.
// This long timeout only recovers a true zombie stream that never terminates.
const STREAM_WATCHDOG_MS = 15 * 60_000;
const MAX_FILE_BYTES = 100 * 1024 * 1024; // 100MB raw per file (server enforces too)
const DEFAULT_MIN_RUN_QUOTA_REMAINING_USD = 5;

// Long threads are slow to load/scroll, so a conversation opens showing only its
// most recent turns; "Load earlier" pages older ones in. A turn = one user
// bubble + one agent bubble.
const INITIAL_TURNS = 5;
const PAGE_TURNS = 10;
const LONG_TASK_CHAR_THRESHOLD = 500;
const LONG_TASK_LINE_THRESHOLD = 6;

// Fold uploaded attachments into the prompt by pointing the agent at the real
// files in its workspace. The agent opens/processes them with its own tools
// (PDF, docx, …) — no browser-side extraction or context-window truncation.
function buildPrompt(uploads: UploadedFile[], typed: string): string {
  if (uploads.length === 0) return typed;
  const list = uploads.map((u) => `- "${u.name}" at ${u.path}`).join("\n");
  const note =
    `The user attached ${uploads.length} file(s), saved on your filesystem:\n${list}\n` +
    `Open and read them with your tools as needed to answer.`;
  return [typed, note].filter((s) => s.trim().length > 0).join("\n\n");
}

function eventText(event: SessionEvent): string {
  const p = event.payload as { text?: string; message?: string };
  return p?.text ?? p?.message ?? "";
}

function eventFiles(event: SessionEvent): AgentFile[] {
  const p = event.payload as { files?: AgentFile[] };
  return Array.isArray(p?.files) ? p.files : [];
}

function eventShowsReferences(event: SessionEvent): boolean {
  return (event.payload as { showReferences?: boolean } | null)?.showReferences === true;
}

function eventPendingFormats(event: SessionEvent): string[] {
  const p = event.payload as { missingFormats?: unknown } | null;
  return Array.isArray(p?.missingFormats) ? p.missingFormats.filter((x): x is string => typeof x === "string") : [];
}

function assistantTextFromEvents(events: SessionEvent[]): string {
  return events.reduce((text, event) => {
    if (event.type === "assistant.final") return eventText(event) || text;
    if (event.type === "assistant.text" || event.type === "process.stdout" || event.type === "process.stderr") {
      return text + eventText(event);
    }
    return text;
  }, "");
}

// Seed the step list (all pending) from an `agent.plan` event.
function eventPlan(event: SessionEvent): { objective: string; steps: AgentStep[] } | null {
  const p = event.payload as { objective?: string; steps?: Array<{ index?: number; total?: number; title?: string }> };
  if (!Array.isArray(p?.steps)) return null;
  const steps = p.steps
    .filter((s) => typeof s?.index === "number")
    .map((s) => ({ index: s.index as number, total: s.total ?? p.steps!.length, title: s.title ?? "", done: false, text: "", status: "pending" as const }));
  return { objective: typeof p.objective === "string" ? p.objective : "", steps };
}

function eventStepStatus(value: unknown): AgentStepStatus {
  return value === "running" || value === "completed" || value === "failed" || value === "partial_timeout" ? value : "completed";
}

// Fold an `agent.step` into the running step list; appends if the plan event
// was missed so a step is never dropped.
function applyStep(steps: AgentStep[], event: SessionEvent): AgentStep[] {
  const p = event.payload as { index?: number; total?: number; title?: string; text?: string; status?: string };
  if (typeof p?.index !== "number") return steps;
  const status = eventStepStatus(p.status);
  const done = status === "completed" || status === "failed" || status === "partial_timeout";
  const incoming = { index: p.index, total: p.total ?? steps.length, title: p.title ?? "", text: p.text ?? "", status, done };
  const next = steps.some((s) => s.index === incoming.index)
    ? steps.map((s) =>
        s.index === incoming.index
          ? {
              ...s,
              done: incoming.done,
              status: incoming.status,
              text: incoming.status === "running" && !incoming.text ? s.text : incoming.text,
              title: incoming.title || s.title
            }
          : s
      )
    : [...steps, incoming];
  return next.sort((a, b) => a.index - b.index);
}

function formatElapsed(ms: number | undefined, lang: string): string {
  if (!Number.isFinite(ms) || !ms || ms < 1000) return lang === "zh" ? "几秒" : "a few seconds";
  const totalSeconds = Math.max(1, Math.round(ms / 1000));
  if (totalSeconds < 60) return lang === "zh" ? `${totalSeconds} 秒` : `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (lang === "zh") return seconds ? `${minutes} 分 ${seconds} 秒` : `${minutes} 分钟`;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

const PROGRESS_KEYS: Record<string, string> = {
  started: "agent.progress.started",
  quota_checked: "agent.progress.quotaChecked",
  workspace_ready: "agent.progress.workspaceReady",
  running: "agent.progress.running",
  background: "agent.progress.background",
  routing: "agent.progress.routing",
  still_working: "agent.progress.stillWorking",
  long_task: "agent.progress.longTask",
  connected: "agent.progress.connected",
  retrying_credential: "agent.progress.retryingCredential",
  refreshing_credential: "agent.progress.refreshingCredential",
  credential_refreshed: "agent.progress.credentialRefreshed",
  runtime_recovering: "agent.progress.runtimeRecovering",
  runtime_recovered: "agent.progress.runtimeRecovered",
  timed_out: "agent.progress.timedOut",
  decomposing: "agent.progress.decomposing",
  subtask_running: "agent.progress.subtaskRunning",
  synthesizing: "agent.progress.synthesizing",
  durable_run: "agent.progress.durableRun",
  reasoning_available: "agent.progress.reasoningAvailable"
};

const PROGRESS_ICONS: Record<string, string> = {
  started: "🟢",
  quota_checked: "💳",
  workspace_ready: "📁",
  running: "🤔",
  background: "🌙",
  routing: "🧭",
  still_working: "⏳",
  long_task: "📚",
  connected: "📡",
  retrying_credential: "🔑",
  refreshing_credential: "🔑",
  credential_refreshed: "✅",
  runtime_recovering: "🧯",
  runtime_recovered: "✅",
  timed_out: "⏱️",
  decomposing: "🧩",
  subtask_running: "⚙️",
  synthesizing: "🧵",
  durable_run: "✨",
  tool_running: "🛠️",
  tool_completed: "✅",
  tool_failed: "⚠️",
  reasoning_available: "🧠"
};

function toolIcon(tool: string, failed = false): string {
  if (failed) return "⚠️";
  const normalized = tool.toLowerCase();
  if (/search|web|browser|extract|curl|fetch/.test(normalized)) return "🔎";
  if (/write|patch|edit|apply/.test(normalized)) return "✏️";
  if (/terminal|execute|code|python|shell|bash|command|npm|bun|pytest|test/.test(normalized)) return "⚙️";
  if (/read|file|pdf|doc|sheet|csv|json|xml/.test(normalized)) return "📄";
  if (/image|vision|screenshot|ocr/.test(normalized)) return "🖼️";
  if (/memory|skill|session/.test(normalized)) return "🧠";
  if (/cron|calendar|time/.test(normalized)) return "⏰";
  return "🛠️";
}

function progressIcon(code: string | undefined, tool: string): string {
  if (code === "tool_running" || code === "tool_completed" || code === "tool_failed") return toolIcon(tool, code === "tool_failed");
  return code ? (PROGRESS_ICONS[code] ?? "•") : "•";
}

function toolLabel(tool: string, lang: string): string {
  const normalized = tool.toLowerCase();
  const zh = lang === "zh";
  if (/web_search|search|web|browser|extract|curl|fetch/.test(normalized)) return zh ? "搜索" : "search";
  if (/write_file|patch|edit|apply/.test(normalized)) return zh ? "修改" : "edit";
  if (/terminal|execute_code|code|python|shell|bash|command|npm|bun|pytest|test/.test(normalized)) return zh ? "执行" : "execute";
  if (/read_file|read|pdf|doc|sheet|csv|json|xml/.test(normalized)) return zh ? "读取" : "read";
  if (/image|vision|screenshot|ocr/.test(normalized)) return zh ? "看图" : "inspect image";
  if (/memory|skill|session/.test(normalized)) return zh ? "调用记忆" : "use memory";
  return zh ? "调用工具" : "use tool";
}

function formatToolProgress(
  code: string,
  tool: string,
  preview: string,
  duration: number | undefined,
  lang: string
): string | null {
  if (code !== "tool_running" && code !== "tool_completed" && code !== "tool_failed") return null;
  const label = toolLabel(tool || "tool", lang);
  const elapsed = Number.isFinite(duration) && duration ? formatElapsed(duration, lang) : "";
  if (lang === "zh") {
    if (code === "tool_running") return preview ? `${label}中：${preview}` : `${label}中`;
    if (code === "tool_failed") return preview ? `${label}失败：${preview}` : `${label}失败`;
    return elapsed ? `${label}完成（${elapsed}）` : `${label}完成`;
  }
  if (code === "tool_running") return preview ? `Running ${label}: ${preview}` : `Running ${label}`;
  if (code === "tool_failed") return preview ? `Failed ${label}: ${preview}` : `Failed ${label}`;
  return elapsed ? `Finished ${label} (${elapsed})` : `Finished ${label}`;
}

function eventProgress(event: SessionEvent, t: (k: string, vars?: Record<string, string | number>) => string, lang: string): AgentProgress {
  const p = event.payload as { code?: string; message?: string; text?: string; elapsedMs?: number; tool?: string; preview?: string; duration?: number };
  const key = p?.code ? PROGRESS_KEYS[p.code] : undefined;
  const toolText = p?.code ? formatToolProgress(p.code, p.tool ?? "", p.preview ?? "", p.duration, lang) : null;
  const text = toolText ?? (key
    ? t(key, { elapsed: formatElapsed(p.elapsedMs, lang) })
    : (p?.text ?? p?.message ?? t("agent.progress.working")));
  return { id: event.id, icon: progressIcon(p?.code, p?.tool ?? ""), text, createdAt: event.createdAt };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

function formatUsd(amount: number): string {
  return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
}

function isLikelyLongTask(typed: string, attachments: File[]): boolean {
  const trimmed = typed.trim();
  if (attachments.length > 0) return true;
  if (trimmed.length >= LONG_TASK_CHAR_THRESHOLD) return true;
  return trimmed.split(/\r?\n/).filter((line) => line.trim().length > 0).length >= LONG_TASK_LINE_THRESHOLD;
}

// Split a stored prompt back into the user's typed text and attachment names
// (buildPrompt appends a "The user attached …" block listing each file path).
function parsePrompt(prompt: string): { text: string; attachments: string[] } {
  const marker = "\n\nThe user attached ";
  const idx = prompt.indexOf(marker);
  if (idx === -1) return { text: prompt, attachments: [] };
  const attachments = [...prompt.slice(idx).matchAll(/- "([^"]+)" at /g)].map((m) => m[1]);
  return { text: prompt.slice(0, idx), attachments };
}

// Turn a loaded conversation (turns oldest-first) into rendered chat messages.
function turnsToMessages(
  turns: Array<{ session: SessionRecord; events: SessionEvent[]; output?: SessionOutput | null }>,
  t: (k: string, vars?: Record<string, string | number>) => string,
  lang: string
): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (const { session, events, output } of turns) {
    const { text, attachments } = parsePrompt(session.prompt ?? session.promptPreview);
    out.push({ id: `u-${session.id}`, role: "user", text: text.trim(), attachments, history: true });
    const result = output?.finalText ?? output?.draftText ?? assistantTextFromEvents(events);
    const failed = terminalFailureEvent(session.status, events);
    const failedPayload = failed?.payload as { message?: string; code?: string; minimumRunQuotaRemainingUsd?: number } | undefined;
    const fileEvents = events.filter((e) => e.type === "agent.files");
    const files = fileEvents.flatMap(eventFiles);
    const showReferences = fileEvents.some(eventShowsReferences);
    const pendingFormats = events.filter((e) => e.type === "agent.deliverable_pending").flatMap(eventPendingFormats);
    const live = session.status === "running" || session.status === "queued";
    const progress = live
      ? events.filter((e) => e.type === "agent.progress").map((e) => eventProgress(e, t, lang)).slice(-6)
      : [];
    // Reconstruct the plan/steps for a still-running turn so a resume after
    // backgrounding shows the work already done (the live stream only replays
    // events past the cursor). Completed turns collapse to just the answer.
    let steps: AgentStep[] = [];
    let objective: string | undefined;
    if (live) {
      for (const e of events) {
        if (e.type === "agent.plan") {
          const plan = eventPlan(e);
          if (plan) {
            steps = plan.steps;
            objective = plan.objective || objective;
          }
        } else if (e.type === "agent.step") {
          steps = applyStep(steps, e);
        }
      }
    }
    out.push({
      id: `a-${session.id}`,
      role: "assistant",
      text: failed ? failedSessionText(result, failedPayload, t) : result,
      files,
      showReferences,
      pendingFormats,
      progress,
      steps: steps.length > 0 ? steps : undefined,
      objective,
      history: true
    });
  }
  return out;
}

export function AgentScreen({
  initialConversationId = null,
  onConversationStarted,
  onSessionsChanged,
  onResumeExpired
}: Props = {}) {
  const { client, account } = useAuth();
  const { t, lang } = useI18n();
  const isLocal = account?.agentMode === "local";

  const [agent, setAgent] = useState<AgentInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [conversationId, setConversationId] = useState<string | null>(initialConversationId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  // History paging: whether older turns remain, the in-flight "load earlier"
  // state, and the oldest currently-loaded session id used as the page cursor.
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [oldestTurnId, setOldestTurnId] = useState<string | null>(null);
  // Mirrors the live conversation id so the switch effect can tell a real
  // parent-driven switch from the parent echoing back an id we just adopted.
  // Starts null (not initialConversationId) so a conversation restored on mount
  // — e.g. resumed after backgrounding — still triggers the load effect below
  // instead of being skipped as an "already on this conversation" no-op.
  const conversationRef = useRef<string | null>(null);
  // Collapse overlapping provisioning polls for the same account. A new account
  // may start immediately without waiting for the old account's request; the
  // account ref prevents that old response from leaking into the new view.
  const agentLoadInFlightRef = useRef<{ accountId: string | null; promise: Promise<void> } | null>(null);
  const activeAgentAccountIdRef = useRef<string | null>(account?.id ?? null);
  activeAgentAccountIdRef.current = account?.id ?? null;
  const [input, setInput] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [previewFile, setPreviewFile] = useState<AgentFile | null>(null);
  const [streaming, setStreaming] = useState(false);
  const closeFilePreview = useCallback(() => setPreviewFile(null), []);

  // Voice dictation (Web Speech API) → append the transcript to the composer.
  const { supported: micSupported, listening, toggle: toggleMic } = useDictation({
    lang,
    onText: (text) => setInput((cur) => (cur.trim() ? `${cur.trimEnd()} ${text}` : text))
  });

  const wsRef = useRef<WebSocket | null>(null);
  const idleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recoveryWsRef = useRef<WebSocket | null>(null);
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Id of the most recently loaded/started session, so the resume path knows
  // which turn to reconnect to after the app returns from the background.
  const lastSessionRef = useRef<string | null>(null);
  // The scrollable transcript container — used to anchor the viewport when
  // "Load earlier" prepends older turns so the view doesn't jump.
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  // Suppresses the next auto-scroll-to-bottom (set when prepending history so a
  // history load doesn't yank the view down to the latest message).
  const skipBottomScrollRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Latest syncConversation closure, so the visibilitychange listener (mounted
  // once) always calls the current version without stale client/account refs.
  const syncRef = useRef<(conversationId: string) => void>(() => {});
  // Same pattern for the expiry callback, so the listener always sees the
  // current prop without re-subscribing.
  const onResumeExpiredRef = useRef<(() => void) | undefined>(undefined);
  onResumeExpiredRef.current = onResumeExpired;

  const loadAgent = useCallback((): Promise<void> => {
    const accountId = account?.id ?? null;
    const existing = agentLoadInFlightRef.current;
    if (existing?.accountId === accountId) return existing.promise;

    const promise = (async () => {
      if (!client || !accountId || isLocal) {
        if (activeAgentAccountIdRef.current === accountId) setLoading(false);
        return;
      }
      try {
        const listing = await client.listAgents(accountId);
        if (activeAgentAccountIdRef.current === accountId) {
          setAgent(listing.cloudHermes[0] ?? null);
          setError(null);
        }
      } catch (e) {
        if (activeAgentAccountIdRef.current === accountId) {
          setError(e instanceof Error ? e.message : t("agent.loadFailed"));
        }
      } finally {
        if (activeAgentAccountIdRef.current === accountId) setLoading(false);
      }
    })();
    agentLoadInFlightRef.current = { accountId, promise };
    void promise.finally(() => {
      if (agentLoadInFlightRef.current?.promise === promise) agentLoadInFlightRef.current = null;
    });
    return promise;
  }, [client, account, isLocal, t]);

  useEffect(() => {
    setAgent(null);
    setError(null);
    setLoading(true);
    void loadAgent();
  }, [loadAgent]);

  // Switch conversations in place when the parent changes the prop (history
  // selection / new chat). The screen is NOT remounted for this — remounting
  // replaced the composer DOM mid-interaction, so a message typed right after
  // the click could land in the dying subtree and silently vanish.
  useEffect(() => {
    if (initialConversationId === conversationRef.current) return; // our own adoption echoed back
    stopRecoveryWatcher();
    // Stop any in-flight stream from the previous conversation.
    if (idleRef.current) clearTimeout(idleRef.current);
    const ws = wsRef.current;
    if (ws) {
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      ws.close();
      wsRef.current = null;
    }
    setStreaming(false);
    setConversationId(initialConversationId);
    setMessages([]);
    setError(null);
    setHasMoreHistory(false);
    setOldestTurnId(null);
    setLoadingMore(false);
    if (!initialConversationId || !client || !account) {
      // Mark as settled so the adoption-echo guard above still works; nothing to load.
      conversationRef.current = initialConversationId;
      setThreadLoading(false);
      return;
    }
    let cancelled = false;
    setThreadLoading(true);
    (async () => {
      try {
        const { turns, hasMore } = await client.getConversation(account.id, initialConversationId, {
          limit: INITIAL_TURNS
        });
        if (!cancelled) {
          // Mark the loaded conversation only after a successful fetch. Doing it
          // synchronously above would make StrictMode's second effect pass (and
          // its cancelled first pass) skip the load and leave the thread empty.
          conversationRef.current = initialConversationId;
          setMessages(turnsToMessages(turns, t, lang));
          setHasMoreHistory(hasMore);
          setOldestTurnId(turns[0]?.session.id ?? null);
          // Reconnect the stream if this conversation's last turn is still
          // running (e.g. restored after the app was backgrounded). The latest
          // turn is always within this first (newest) page.
          resumeIfRunning(turns);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t("common.failed"));
      } finally {
        if (!cancelled) setThreadLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId]);

  // Poll while the container is still provisioning.
  useEffect(() => {
    if (isLocal || !agent || (agent.status !== "provisioning" && agent.status !== "suspended")) return;
    const timer = setInterval(() => void loadAgent(), 4000);
    return () => clearInterval(timer);
  }, [isLocal, agent, loadAgent]);

  useEffect(() => {
    // Prepending older turns ("Load earlier") changes `messages` too; don't yank
    // the view to the bottom in that case — loadEarlier restores the scroll
    // position itself.
    if (skipBottomScrollRef.current) {
      skipBottomScrollRef.current = false;
      return;
    }
    if (transcriptRef.current) scrollTranscriptToBottom(transcriptRef.current);
  }, [messages]);

  // Auto-grow the composer textarea with its content (capped by CSS max-height).
  // Only show the scrollbar once content genuinely exceeds the cap — otherwise a
  // sub-pixel rounding gap between scrollHeight and the set height (box-sizing:
  // border-box + padding) keeps overflow-y:auto's scrollbar permanently visible.
  const resizeComposer = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const maxHeight = parseFloat(getComputedStyle(el).maxHeight) || Infinity;
    const next = el.scrollHeight;
    el.style.height = `${Math.min(next, maxHeight)}px`;
    // Round up the cap by 1px so a sub-pixel content/height delta doesn't trip the
    // scrollbar; only overflow (and scroll) once we're truly past the cap.
    el.style.overflowY = next > maxHeight + 1 ? "auto" : "hidden";
  }, []);

  // Recompute on content change.
  useEffect(() => {
    resizeComposer();
  }, [input, resizeComposer]);

  // Recompute on first paint after web fonts load and on viewport changes (mobile
  // rotation / on-screen keyboard) — the [input] effect alone misses these, which is
  // why a stale height left a scrollbar on both web and mobile.
  useEffect(() => {
    resizeComposer();
    window.addEventListener("resize", resizeComposer);
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(resizeComposer).catch(() => {});
    return () => window.removeEventListener("resize", resizeComposer);
  }, [resizeComposer]);

  useEffect(() => {
    return () => {
      stopRecoveryWatcher();
      // Detach handlers before closing so unmount (e.g. switching conversations
      // mid-stream) doesn't fire finalize → setState on an unmounted tree.
      const ws = wsRef.current;
      if (ws) {
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      }
      if (idleRef.current) clearTimeout(idleRef.current);
    };
  }, []);

  // Returning from the background kills the stream socket but the agent kept
  // running server-side. When the tab becomes visible again, either resume the
  // active conversation (re-fetch + reconnect an in-flight turn) or, if the
  // resume window elapsed while away, drop to a fresh new chat — matching the
  // reload/mount behavior so a long-lived tab expires the same as an evicted
  // one. Registered once; reads the latest closures via refs to avoid stale
  // client/account.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const id = conversationRef.current;
      if (!id) return;
      // activeConversation.load() clears + returns null once past the window.
      if (activeConversation.load() === null) onResumeExpiredRef.current?.();
      else syncRef.current(id);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  function stopRecoveryWatcher() {
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = null;
    const ws = recoveryWsRef.current;
    recoveryWsRef.current = null;
    if (ws) {
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      ws.close();
    }
  }

  function watchForRecovery(sessionId: string, watchedConversationId: string, afterEventId: number) {
    if (!client || !account) return;
    stopRecoveryWatcher();
    const ws = new WebSocket(client.accountEventsUrl(account.id, afterEventId));
    recoveryWsRef.current = ws;
    recoveryTimerRef.current = setTimeout(() => {
      stopRecoveryWatcher();
      void syncConversation(watchedConversationId);
    }, RESULT_RECOVERY_WATCH_MS);
    ws.onmessage = (message) => {
      const event = JSON.parse(message.data as string) as SessionEvent;
      const action = recoveryEventAction(event, sessionId);
      if (action === "replace_result") {
        const text = eventText(event);
        if (text) {
          const assistantId = `a-${sessionId}`;
          setMessages((cur) => cur.map((item) => (item.id === assistantId ? { ...item, text } : item)));
        }
      } else if (action === "reload_conversation") {
        stopRecoveryWatcher();
        void syncConversation(watchedConversationId);
        onSessionsChanged?.();
      }
    };
    ws.onerror = () => {
      // Durable events are replayed from afterEventId when the conversation is
      // reloaded by the timeout/visibility fallback.
    };
  }

  function finalize() {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = null;
    wsRef.current?.close();
    wsRef.current = null;
    setStreaming(false);
    setMessages((cur) => cur.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
    onSessionsChanged?.(); // refresh history so the turn's final status shows
  }

  function armIdle() {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = setTimeout(finalize, STREAM_WATCHDOG_MS);
  }

  // Open the event stream for a session and fold its deltas into the assistant
  // bubble `assistantId`. `afterEventId` lets a resume skip events already
  // rendered (0 = from the start). Shared by send() and the resume path.
  function attachStream(sessionId: string, assistantId: string, afterEventId: number) {
    if (!client || !account) return;
    lastSessionRef.current = sessionId;
    setStreaming(true);
    const ws = new WebSocket(client.sessionStreamUrl(account.id, sessionId, afterEventId));
    let terminalEventReceived = false;
    wsRef.current = ws;
    armIdle();
    ws.onmessage = (msg) => {
      armIdle();
      const event = JSON.parse(msg.data as string) as SessionEvent;
      if (event.type === "assistant.text" || event.type === "process.stdout" || event.type === "process.stderr") {
        const delta = eventText(event);
        if (delta) {
          setMessages((cur) => cur.map((m) => (m.id === assistantId ? { ...m, text: m.text + delta } : m)));
        }
      } else if (event.type === "assistant.final") {
        const text = eventText(event);
        if (text) {
          setMessages((cur) => cur.map((m) => (m.id === assistantId ? { ...m, text } : m)));
        }
      } else if (
        event.type === "session.completed" ||
        event.type === "session.failed" ||
        event.type === "session.cancelled"
      ) {
        terminalEventReceived = true;
        if (event.type === "session.failed") {
          const p = event.payload as { message?: string; code?: string };
          setMessages((cur) =>
            cur.map((m) => (m.id === assistantId ? { ...m, text: failedSessionText(m.text, p, t) } : m))
          );
          const watchedConversationId = conversationRef.current;
          finalize();
          if (watchedConversationId) watchForRecovery(event.sessionId, watchedConversationId, event.id);
        } else {
          finalize();
        }
      } else if (event.type === "agent.plan") {
        const plan = eventPlan(event);
        if (plan) {
          setMessages((cur) =>
            cur.map((m) => (m.id === assistantId ? { ...m, steps: plan.steps, objective: plan.objective || m.objective } : m))
          );
        }
      } else if (event.type === "agent.step") {
        setMessages((cur) =>
          cur.map((m) => (m.id === assistantId ? { ...m, steps: applyStep(m.steps ?? [], event) } : m))
        );
      } else if (event.type === "agent.progress") {
        const progress = eventProgress(event, t, lang);
        setMessages((cur) =>
          cur.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  progress: [...(m.progress ?? []).filter((item) => item.id !== progress.id), progress].slice(-6)
                }
              : m
          )
        );
      } else if (event.type === "agent.files") {
        const files = eventFiles(event);
        if (files.length > 0) {
          setMessages((cur) =>
            cur.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    files: [...(m.files ?? []), ...files.filter((file) => !(m.files ?? []).some((existing) => existing.path === file.path))],
                    showReferences: m.showReferences || eventShowsReferences(event)
                  }
                : m
            )
          );
        }
      } else if (event.type === "agent.deliverable_pending") {
        const pending = eventPendingFormats(event);
        if (pending.length > 0) {
          setMessages((cur) => cur.map((m) => (m.id === assistantId ? { ...m, pendingFormats: pending } : m)));
        }
      }
    };
    ws.onerror = () => armIdle();
    ws.onclose = () => {
      const shouldRefreshConversation = !terminalEventReceived;
      finalize();
      if (!shouldRefreshConversation) return;
      const id = conversationRef.current;
      if (!id) return;
      window.setTimeout(() => {
        void syncConversation(id);
      }, 1000);
    };
  }

  // If the last turn of a loaded conversation is still running, reconnect its
  // stream so the in-flight answer keeps arriving (the cloud agent emits its
  // reply only at completion, so this is what surfaces a result finished while
  // the app was backgrounded).
  function resumeIfRunning(turns: Array<{ session: SessionRecord; events: SessionEvent[]; output?: SessionOutput | null }>) {
    const last = turns[turns.length - 1];
    if (!last) return;
    lastSessionRef.current = last.session.id;
    if (last.session.status === "running" || last.session.status === "queued") {
      const assistantId = `a-${last.session.id}`;
      const maxEventId = last.events.reduce((mx, e) => Math.max(mx, e.id), 0);
      setMessages((cur) => cur.map((m) => (m.id === assistantId ? { ...m, streaming: true } : m)));
      attachStream(last.session.id, assistantId, maxEventId);
    }
  }

  // Page in the turns immediately older than the oldest currently-loaded one,
  // prepending them while keeping the viewport anchored on the message the user
  // is reading (the transcript otherwise jumps when content is added on top).
  async function loadEarlier() {
    if (!client || !account || !conversationId || loadingMore || !hasMoreHistory) return;
    setLoadingMore(true);
    const scroller = transcriptRef.current;
    const prevHeight = scroller?.scrollHeight ?? 0;
    const prevTop = scroller?.scrollTop ?? 0;
    try {
      const { turns, hasMore } = await client.getConversation(account.id, conversationId, {
        limit: PAGE_TURNS,
        before: oldestTurnId
      });
      if (turns.length === 0) {
        setHasMoreHistory(false);
        return;
      }
      const older = turnsToMessages(turns, t, lang);
      skipBottomScrollRef.current = true; // the messages change must not scroll to bottom
      setMessages((cur) => [...older, ...cur]);
      setHasMoreHistory(hasMore);
      setOldestTurnId(turns[0]?.session.id ?? oldestTurnId);
      // Restore scroll after the prepended turns paint so the previously-visible
      // message stays put (new height − old height = added content above).
      requestAnimationFrame(() => {
        if (scroller) scroller.scrollTop = prevTop + (scroller.scrollHeight - prevHeight);
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.failed"));
    } finally {
      setLoadingMore(false);
    }
  }

  // Re-fetch a conversation's transcript and resume a running turn. Used on
  // return-from-background (the page stayed alive but the socket was killed).
  // Refetches at least as many turns as are currently shown so an expanded
  // history isn't collapsed back to the initial window on a tab switch.
  async function syncConversation(id: string) {
    if (!client || !account || wsRef.current) return; // a live stream already covers us
    try {
      const loadedTurns = messages.filter((m) => m.role === "user").length;
      const { turns, hasMore } = await client.getConversation(account.id, id, {
        limit: Math.max(INITIAL_TURNS, loadedTurns)
      });
      setMessages(turnsToMessages(turns, t, lang));
      setHasMoreHistory(hasMore);
      setOldestTurnId(turns[0]?.session.id ?? null);
      resumeIfRunning(turns);
    } catch {
      // transient (offline / token); the next visibility change retries
    }
  }
  syncRef.current = syncConversation;

  function addFiles(picked: FileList | null) {
    if (!picked || picked.length === 0) return;
    setError(null);
    setFiles((cur) => {
      const next = [...cur];
      for (const f of Array.from(picked)) {
        if (!next.some((e) => e.name === f.name && e.size === f.size)) next.push(f);
      }
      return next;
    });
  }

  function removeFile(idx: number) {
    setFiles((cur) => cur.filter((_, i) => i !== idx));
  }

  const fetchGeneratedFile = useCallback(
    async (file: AgentFile) => {
      if (!client || !account || !agent) throw new Error("agent file unavailable");
      return client.downloadAgentFile(account.id, agent.id, file.path);
    },
    [account, agent, client]
  );

  const openLinkedGeneratedFile = useCallback(
    (linkedFile: AgentFile, context: MarkdownFileLinkContext) => {
      setError(null);
      const file = resolveLinkedAgentFile(linkedFile, context.files);
      if (!file) {
        setError(t("agent.preview.unavailable"));
        return;
      }
      setPreviewFile(file);
    },
    [t]
  );

  async function downloadGeneratedFile(file: AgentFile): Promise<void> {
    try {
      const blob = await fetchGeneratedFile(file);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.failed"));
    }
  }

  async function send() {
    if (!client || !account || !agent || streaming) return;
    const typed = input.trim();
    if (!typed && files.length === 0) return;
    if (files.some((f) => f.size > MAX_FILE_BYTES)) {
      setError(t("agent.tooLarge"));
      return;
    }

    const pending = files;
    const attachmentNames = pending.map((f) => f.name);
    const localProgressId = -Date.now();
    const initialProgress: AgentProgress[] | undefined = isLikelyLongTask(typed, pending)
      ? [
          {
            id: localProgressId,
            icon: "🌙",
            text: pending.length > 0 ? t("agent.backgroundHintSubmitting") : t("agent.backgroundHint"),
            createdAt: new Date().toISOString()
          }
        ]
      : undefined;
    setError(null);
    setInput("");
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";

    const assistantId = `a-${Date.now()}`;
    setMessages((cur) => [
      ...cur,
      { id: `u-${Date.now()}`, role: "user", text: typed, attachments: attachmentNames },
      { id: assistantId, role: "assistant", text: "", progress: initialProgress, streaming: true }
    ]);
    setStreaming(true);

    try {
      const billing = await client.getBillingPlans();
      const minimumRunQuotaRemainingUsd =
        billing.minimumRunQuotaRemainingUsd ?? DEFAULT_MIN_RUN_QUOTA_REMAINING_USD;
      const balanceUsd = billing.balanceUsd ?? Math.max(0, billing.currentQuotaUsd - billing.quotaUsedUsd);
      if (billing.enabled && (!billing.hasKey || balanceUsd <= 0)) {
        setMessages((cur) =>
          cur.map((m) => (m.id === assistantId ? { ...m, text: `⚠ ${t("agent.quotaExceeded")}`, streaming: false } : m))
        );
        setStreaming(false);
        return;
      }
      if (billing.enabled && balanceUsd <= minimumRunQuotaRemainingUsd) {
        setMessages((cur) =>
          cur.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  text: `⚠ ${t("agent.quotaLow", { minimum: formatUsd(minimumRunQuotaRemainingUsd) })}`,
                  streaming: false
                }
              : m
          )
        );
        setStreaming(false);
        return;
      }
    } catch {
      // Non-fatal: the backend performs the same quota preflight before Hermes.
    }

    // Upload attachments into the agent's workspace, then point the prompt at
    // the resulting paths so the agent reads the real files.
    let prompt: string;
    try {
      const uploads: UploadedFile[] = [];
      for (const f of pending) {
        uploads.push(await client.uploadFile(account.id, agent.id, f));
      }
      prompt = buildPrompt(uploads, typed);
    } catch (e) {
      setMessages((cur) =>
        cur.map((m) =>
          m.id === assistantId
            ? { ...m, text: `⚠ ${e instanceof Error ? e.message : t("common.failed")}`, streaming: false }
            : m
        )
      );
      setStreaming(false);
      return;
    }

    try {
      const { session } = await client.startSession(
        account.id,
        { executionTarget: "cloud_hermes", agentInstanceId: agent.id },
        prompt,
        conversationId
      );
      if (initialProgress) {
        setMessages((cur) =>
          cur.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  progress: (m.progress ?? []).map((item) =>
                    item.id === localProgressId ? { ...item, text: t("agent.backgroundHint") } : item
                  )
                }
              : m
          )
        );
      }
      // Adopt the conversation id on the first turn so the next message continues
      // it. Update the ref first so the parent echoing the id back through
      // initialConversationId is a no-op rather than a thread reset.
      if (!conversationId && session.conversationId) {
        conversationRef.current = session.conversationId;
        setConversationId(session.conversationId);
        onConversationStarted?.(session.conversationId);
      }
      onSessionsChanged?.(); // surface the new turn in the history drawer
      attachStream(session.id, assistantId, 0);
    } catch (e) {
      setMessages((cur) =>
        cur.map((m) =>
          m.id === assistantId ? { ...m, text: `⚠ ${e instanceof Error ? e.message : t("common.failed")}`, streaming: false } : m
        )
      );
      setStreaming(false);
    }
  }

  if (isLocal) {
    return (
      <main className="main">
        <section className="panel">
          <h2 className="section-title">{t("agent.localTitle")}</h2>
          <p className="muted">{t("agent.localBody")}</p>
        </section>
      </main>
    );
  }

  if (loading) {
    return (
      <div className="center">
        <div className="spinner" />
      </div>
    );
  }

  const status = agent?.status;
  const notReady = !agent || status !== "ready";
  const statusLabel = status ? t(`status.${status}`) : "";

  const renderGeneratedFile = (file: AgentFile) => (
    filePreviewKind(file) === "video" ? (
      <GeneratedVideoPreview
        key={file.path}
        file={file}
        fetchFile={fetchGeneratedFile}
        downloadFile={downloadGeneratedFile}
        labels={{
          download: t("agent.preview.download"),
          load: t("agent.preview.loadVideo"),
          loading: t("agent.preview.loadingVideo"),
          retry: t("common.retry"),
          tooLarge: t("agent.preview.videoTooLarge"),
          unavailable: t("agent.preview.unavailable")
        }}
      />
    ) : (
    <div key={file.path} className={`generated-file-entry is-${filePreviewKind(file)}`}>
      <div className="generated-file-row">
        <button
          type="button"
          className="generated-file"
          title={t("agent.preview.open", { name: file.name })}
          onClick={() => setPreviewFile(file)}
        >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
        <span className="generated-file-copy">
          <span className="generated-file-name">{file.name}</span>
          <span className="generated-file-meta">
            {t(`agent.preview.kind.${filePreviewKind(file)}`)} · {formatBytes(file.bytes)}
          </span>
        </span>
        </button>
        <button
          type="button"
          className="generated-file-download"
          aria-label={t("agent.downloadFile", { name: file.name })}
          title={t("agent.downloadFile", { name: file.name })}
          onClick={() => void downloadGeneratedFile(file)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
            <path d="M12 3v12" />
            <path d="m7 10 5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
        </button>
      </div>
    </div>
    )
  );

  return (
    <main className="main chat">
      {error ? <div className="error-banner">{error}</div> : null}
      <GeneratedFilePreview
        file={previewFile}
        fetchFile={fetchGeneratedFile}
        downloadFile={downloadGeneratedFile}
        onClose={closeFilePreview}
        labels={{
          close: t("common.close"),
          download: t("agent.preview.download"),
          htmlSafety: t("agent.preview.htmlSafety"),
          htmlLive: t("agent.preview.htmlLive"),
          imageTooLarge: (name: string) => t("agent.preview.imageTooLarge", { name }),
          imageUnavailable: (name: string) => t("agent.preview.imageUnavailable", { name }),
          kind: {
            markdown: t("agent.preview.kind.markdown"),
            html: t("agent.preview.kind.html"),
            image: t("agent.preview.kind.image"),
            video: t("agent.preview.kind.video"),
            pdf: t("agent.preview.kind.pdf"),
            unsupported: t("agent.preview.kind.unsupported")
          },
          loading: t("agent.preview.loading"),
          outline: t("agent.preview.outline"),
          pdfPage: (page: number, total: number) => t("agent.preview.pdfPage", { page, total }),
          pdfPrev: t("agent.preview.pdfPrev"),
          pdfNext: t("agent.preview.pdfNext"),
          remoteImageBlocked: t("agent.preview.remoteImageBlocked"),
          retry: t("common.retry"),
          tooLarge: t("agent.preview.tooLarge"),
          unavailable: t("agent.preview.unavailable"),
          unsupported: t("agent.preview.unsupported")
        }}
      />

      {notReady ? (
        <section className="panel provisioning">
          {status === "failed" ? (
            <>
              <h2 className="section-title">{t("agent.provFailedTitle")}</h2>
              <p className="muted">{t("agent.provFailedBody")}</p>
              <button type="button" className="btn-secondary" onClick={() => void loadAgent()}>
                {t("agent.recheck")}
              </button>
            </>
          ) : (
            <>
              <div className="spinner" />
              <h2 className="section-title" style={{ marginTop: 14 }}>
                {t("agent.provisioningTitle")}
              </h2>
              <p className="muted">{agent ? t("agent.provisioningBody", { status: statusLabel }) : t("agent.settingUp")}</p>
            </>
          )}
        </section>
      ) : (
        <>
          <div className="transcript" ref={transcriptRef}>
            {threadLoading ? (
              <div className="center" style={{ height: 80 }}>
                <div className="spinner" />
              </div>
            ) : messages.length === 0 ? (
              <p className="muted empty-hint">{t("agent.readyHint")}</p>
            ) : (
              <>
                {hasMoreHistory ? (
                  <div className="load-earlier">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => void loadEarlier()}
                      disabled={loadingMore}
                    >
                      {loadingMore ? <span className="spinner inline" /> : t("agent.loadEarlier")}
                    </button>
                  </div>
                ) : null}
                {messages.map((m) => (
                <div key={m.id} className={`bubble ${m.role}`}>
                  <span className="bubble-role">{m.role === "user" ? t("agent.roleYou") : t("agent.roleAgent")}</span>
                  {m.steps && m.steps.length > 0 ? (
                    <>
                      <div className="agent-steps" aria-label={t("agent.plan.label")}>
                        <span className="agent-steps-title">{t("agent.plan.title")}</span>
                        {m.objective ? <p className="agent-steps-objective">{m.objective}</p> : null}
                        {m.steps.map((step, i) => {
                          const inferredRunning = !step.done && m.steps!.slice(0, i).every((prev) => prev.done);
                          const status = step.status === "pending" && inferredRunning ? "running" : step.status;
                          const terminal = status === "completed" || status === "failed" || status === "partial_timeout";
                          return (
                            <div key={step.index} className={`agent-step ${status}`}>
                              <span className="agent-step-marker" aria-hidden="true">
                                {status === "completed" ? "✓" : status === "failed" ? "!" : status === "partial_timeout" ? "~" : status === "running" ? <span className="spinner inline" /> : "•"}
                              </span>
                              <div className="agent-step-body">
                                <span className="agent-step-title">
                                  {t("agent.steps.item", { n: step.index + 1, total: step.total, title: step.title })}
                                </span>
                                {terminal && step.text ? <div className="agent-step-text">{stripCitationMarkers(step.text)}</div> : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {m.streaming && m.progress && m.progress.length > 0 ? (
                        <div className="agent-progress agent-progress-under-steps" aria-label={t("agent.progressLabel")}>
                          {m.progress.map((item) => (
                            <div key={item.id} className="agent-progress-item">
                              <span className="agent-progress-icon" aria-hidden="true">{item.icon}</span>
                              <span>{item.text}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                  {m.text ? (
                    m.role === "assistant" ? (
                      <MarkdownMessage
                        text={m.text}
                        streaming={m.streaming}
                        linkedFiles={m.files}
                        onFileLink={openLinkedGeneratedFile}
                      />
                    ) : (
                      <p className="bubble-text">
                        {m.text}
                        {m.streaming ? <span className="caret" /> : null}
                      </p>
                    )
                  ) : m.streaming && !(m.steps && m.steps.length > 0) ? (
                    m.progress && m.progress.length > 0 ? (
                      <div className="agent-progress" aria-label={t("agent.progressLabel")}>
                        {m.progress.map((item) => (
                          <div key={item.id} className="agent-progress-item">
                            <span className="agent-progress-icon" aria-hidden="true">{item.icon}</span>
                            <span>{item.text}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="bubble-text thinking">
                        <span className="thinking-dots" aria-hidden="true">
                          <span />
                          <span />
                          <span />
                        </span>
                        <span className="thinking-label">{t("agent.thinking")}</span>
                      </p>
                    )
                  ) : null}
                  {m.attachments && m.attachments.length > 0 ? (
                    <div className="attach-list">
                      {m.attachments.map((name) => (
                        <span key={name} className="attach-chip readonly">
                          📎 {name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {m.pendingFormats && stillPendingFormats(m.pendingFormats, m.files ?? []).length > 0 ? (
                    <p className="deliverable-pending">
                      {t("agent.deliverablePending", { formats: stillPendingFormats(m.pendingFormats, m.files ?? []).join("、") })}
                    </p>
                  ) : null}
                  {m.files && m.files.length > 0 ? (
                    <div className="generated-files" aria-label={t("agent.generatedFiles")}>
                      <span className="generated-files-title">{t("agent.filesTitle")}</span>
                      <div className="generated-files-list">
                        {splitFilesByRole(m.files).deliverables.map(renderGeneratedFile)}
                      </div>
                      {splitFilesByRole(m.files).references.length > 0 ? (
                        <details className="process-files reference-files" open={m.showReferences || undefined}>
                          <summary title={t("agent.referenceFilesHint")}>
                            {t("agent.referenceFiles", { count: splitFilesByRole(m.files).references.length })}
                          </summary>
                          <div className="generated-files-list">
                            {splitFilesByRole(m.files).references.map(renderGeneratedFile)}
                          </div>
                        </details>
                      ) : null}
                      {splitFilesByRole(m.files).process.length > 0 ? (
                        <details className="process-files">
                          <summary title={t("agent.processFilesHint")}>
                            {t("agent.processFiles", { count: splitFilesByRole(m.files).process.length })}
                          </summary>
                          <div className="generated-files-list">
                            {splitFilesByRole(m.files).process.map(renderGeneratedFile)}
                          </div>
                        </details>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                ))}
              </>
            )}
          </div>

          <div className="composer">
            {files.length > 0 ? (
              <div className="attach-list pending">
                {files.map((f, i) => (
                  <span key={`${f.name}-${i}`} className="attach-chip">
                    📎 {f.name}
                    <button
                      type="button"
                      className="attach-remove"
                      aria-label={t("agent.removeAttachment")}
                      title={t("agent.removeAttachment")}
                      onClick={() => removeFile(i)}
                      disabled={streaming}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <div className="composer-row">
              <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} />
              <button
                type="button"
                className="composer-add"
                title={t("agent.attach")}
                aria-label={t("agent.attach")}
                onClick={() => fileInputRef.current?.click()}
                disabled={streaming}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
              <div className="composer-pill">
                <textarea
                  ref={textareaRef}
                  className="textarea"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.shiftKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder={t("agent.composerPlaceholder")}
                  disabled={streaming}
                  rows={1}
                />
                {micSupported ? (
                  <button
                    type="button"
                    className={`composer-mic ${listening ? "listening" : ""}`}
                    onClick={toggleMic}
                    disabled={streaming}
                    aria-label={t("agent.voice")}
                    aria-pressed={listening}
                    title={t("agent.voice")}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <path d="M12 19v3" />
                    </svg>
                  </button>
                ) : null}
                <button
                  type="button"
                  className="composer-send"
                  onClick={() => void send()}
                  disabled={streaming || (!input.trim() && files.length === 0)}
                  aria-label={t("agent.send")}
                  title={t("agent.send")}
                >
                  {streaming ? (
                    <span className="spinner inline" />
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 19V5M5 12l7-7 7 7" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
